import { BanknoteArrowDown, ExternalLinkIcon } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { Timestamp } from '~/components/component-library/Timestamp'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { CheckCircle } from '~/icons/CheckCircle'
import { OctagonAlert } from '~/icons/OctagonAlert'
import { BackendTransaction, TransactionStatus } from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'
import { getBridgeReceipt } from '~/utils/bridgeReceipt'
import { parseTransactionMetadata } from '~/utils/parseTransactionMetadata'

import type { TransactionTimelineRowProps } from '~/components/txn-display/timeline/components/TimelineRow'
import type { BridgeData } from '~/types'

const MANUAL_WITHDRAW_REQUIRED = 'manual-withdraw-required'

const isManualWithdrawRequired = (failureReason?: string | null) =>
  failureReason?.toLowerCase().replaceAll('_', '-') === MANUAL_WITHDRAW_REQUIRED

export const buildBridgeFailureRows = ({
  transactionId,
  sourceChainId,
  bridgeTransactionMetadata,
}: {
  transactionId: string
  sourceChainId: number
  bridgeTransactionMetadata: BridgeData
}): TransactionTimelineRowProps[] => {
  const refundExplorerUrl = getExplorerTransactionLinkChainId(
    bridgeTransactionMetadata.refundTransaction?.hash,
    sourceChainId,
  )
  const receiptUrl = getBridgeReceipt(bridgeTransactionMetadata)?.url
  const failureReason =
    bridgeTransactionMetadata.failureReason ??
    bridgeTransactionMetadata.refundTransaction?.failureReason
  const manualWithdrawRequired = isManualWithdrawRequired(failureReason)
  const hasRefundTransaction = !!bridgeTransactionMetadata.refundTransaction

  const failureContent = manualWithdrawRequired
    ? 'Failed - manual withdrawal required'
    : `Failed${failureReason ? ` - ${failureReason}` : ''}`

  const rows: TransactionTimelineRowProps[] = [
    {
      key: `status-${transactionId}-bridge-failed`,
      icon: <OctagonAlert className="size-4 fill-orange-500" />,
      content: failureContent,
      meta: bridgeTransactionMetadata.refundTransaction ? (
        <Timestamp
          type="relative"
          timestamp={bridgeTransactionMetadata.refundTransaction.time}
          abbreviate
        />
      ) : (
        ''
      ),
    },
  ]

  if (hasRefundTransaction) {
    rows.push({
      key: `status-${transactionId}-bridge-refund-transaction`,
      icon: <BanknoteArrowDown className="size-4 text-secondary" />,
      content: refundExplorerUrl ? (
        <SplitsLink
          path={refundExplorerUrl}
          target="tab"
          className="hover:underline"
        >
          <div className="flex items-center gap-1">
            <p>Refunded</p>
            <ExternalLinkIcon className="size-3" />
          </div>
        </SplitsLink>
      ) : (
        'Refunded'
      ),
      meta: bridgeTransactionMetadata.refundTransaction ? (
        <Timestamp
          type="relative"
          timestamp={bridgeTransactionMetadata.refundTransaction.time}
          abbreviate
        />
      ) : (
        ''
      ),
    })
    return rows
  }

  if (manualWithdrawRequired) {
    rows.push({
      key: `status-${transactionId}-bridge-manual-withdraw-required`,
      icon: <BanknoteArrowDown className="size-4 text-secondary" />,
      content: receiptUrl ? (
        <div className="flex min-w-0 items-center gap-1">
          <span>Contact Splits team to recover funds</span>
          <SplitsLink
            path={receiptUrl}
            target="tab"
            className="inline-flex items-center gap-1 hover:underline"
          >
            <span>Relay receipt</span>
            <ExternalLinkIcon className="size-3" />
          </SplitsLink>
        </div>
      ) : (
        'Contact Splits team to recover funds'
      ),
      meta: '',
    })
    return rows
  }

  rows.push({
    key: `status-${transactionId}-bridge-no-refund-transaction`,
    icon: <OctagonAlert className="size-4 fill-orange-500" />,
    content: receiptUrl ? (
      <SplitsLink path={receiptUrl} target="tab" className="hover:underline">
        <div className="flex items-center gap-1">
          <p>Failed</p>
          <ExternalLinkIcon className="size-3" />
        </div>
      </SplitsLink>
    ) : (
      'Failed'
    ),
    meta: '',
  })

  return rows
}

export function useBridgeRow({
  transaction,
}: {
  transaction: BackendTransaction
}): TransactionTimelineRowProps[] {
  const { smartAccount } = useSmartAccount(transaction.smartAccountAddress)

  if (!smartAccount) return []

  if (transaction.isMerkleTransaction) return []

  const transactionMetadata = parseTransactionMetadata(
    transaction.transactionMetadata,
  )

  if (!transactionMetadata.bridgeData) return []

  const bridgeTransactionMetadata = transactionMetadata.bridgeData

  const status = bridgeTransactionMetadata?.status

  if (!status) return []

  if (transaction.status !== TransactionStatus.Succeeded) return []

  switch (status) {
    case 'SUCCESS': {
      const explorerUrl = getExplorerTransactionLinkChainId(
        bridgeTransactionMetadata.destinationTransaction?.hash,
        bridgeTransactionMetadata.destinationChainId,
      )
      return [
        {
          key: `status-${transaction.id}-bridge-succeeded`,
          icon: <CheckCircle className="size-4 text-green-500" />,
          content: explorerUrl ? (
            <SplitsLink
              path={explorerUrl}
              target="tab"
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <p>Completed</p>
                <ExternalLinkIcon className="size-3" />
              </div>
            </SplitsLink>
          ) : (
            'Completed'
          ),
          meta: bridgeTransactionMetadata.destinationTransaction ? (
            <Timestamp
              type="relative"
              timestamp={bridgeTransactionMetadata.destinationTransaction.time}
              abbreviate
            />
          ) : (
            ''
          ),
        },
      ]
    }
    case 'FAILURE': {
      return buildBridgeFailureRows({
        transactionId: transaction.id,
        sourceChainId: transaction.chainId,
        bridgeTransactionMetadata,
      })
    }
    case 'PENDING': {
      return [
        {
          key: `status-${transaction.id}-bridge-pending`,
          icon: <LoadingSpinner size="lg" thickness="thin" variant="default" />,
          content: 'Confirming',
          meta: '',
        },
      ]
    }
    default:
      return []
  }
}
